import React, { useContext, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Button, Card, Tabs, Tag } from 'antd';
import {
    AppstoreOutlined,
    GiftOutlined,
    PlusOutlined,
} from '@ant-design/icons';

import PageHeader from '../../components/PageHeader';
import TabRewards from './components/TabRewards';
import TabCategories from './components/TabCategories';
import { ThemeContext } from '../../contexts/ThemeContext';
import { poeValorComPontosEmMil } from '../../utils/Conversores';

interface IProps {
    defaultTab?: 'rewards' | 'categories';
}

export default function Rewards({ defaultTab }: IProps) {
    const { theme } = useContext(ThemeContext);
    const isDark = theme === 'dark';
    const navigate = useNavigate();
    const [searchParams, setSearchParams] = useSearchParams();

    const paramTab = searchParams.get('tab');
    const initialTab = defaultTab || (paramTab === 'categories' ? 'categories' : 'rewards');

    const [activeTab, setActiveTab] = useState<string>(initialTab);
    const [totalRewards, setTotalRewards] = useState<number>(0);
    const [totalCategories, setTotalCategories] = useState<number>(0);

    const handleTabChange = (key: string) => {
        setActiveTab(key);
        setSearchParams({ tab: key });
    };

    const cardBg = isDark ? '#141414' : '#ffffff';
    const cardBorder = isDark ? '#262626' : '#eaedf1';

    const items = [
        {
            key: 'rewards',
            label: (
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontWeight: 600, fontSize: 13.5 }}>
                    <GiftOutlined />
                    Recompensas
                </span>
            ),
            children: <TabRewards onTotalChange={setTotalRewards} />,
        },
        {
            key: 'categories',
            label: (
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontWeight: 600, fontSize: 13.5 }}>
                    <AppstoreOutlined />
                    Categorias
                </span>
            ),
            children: <TabCategories onTotalChange={setTotalCategories} />,
        },
    ];

    return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
            <PageHeader
                icon={<GiftOutlined />}
                iconBg="linear-gradient(135deg, #10b981 0%, #059669 100%)"
                title="Recompensas"
                tag={
                    <Tag color="green">
                        {activeTab === 'rewards'
                            ? `${poeValorComPontosEmMil(totalRewards)} recompensas`
                            : `${poeValorComPontosEmMil(totalCategories)} categorias`}
                    </Tag>
                }
                subtitle="Gerencie o catálogo de produtos para resgate de pontos e suas respectivas categorias no programa"
                actions={
                    activeTab === 'rewards' ? (
                        <Button
                            type="primary"
                            icon={<PlusOutlined />}
                            onClick={() => navigate('/recompensas/criar')}
                            style={{
                                display: 'inline-flex',
                                alignItems: 'center',
                                gap: 6,
                                fontWeight: 600,
                                height: 38,
                                borderRadius: 8,
                            }}
                        >
                            Nova Recompensa
                        </Button>
                    ) : (
                        <Button
                            type="primary"
                            icon={<PlusOutlined />}
                            onClick={() => navigate('/categorias/criar')}
                            style={{
                                display: 'inline-flex',
                                alignItems: 'center',
                                gap: 6,
                                fontWeight: 600,
                                height: 38,
                                borderRadius: 8,
                            }}
                        >
                            Nova Categoria
                        </Button>
                    )
                }
            />

            <Card
                style={{
                    background: cardBg,
                    border: `1px solid ${cardBorder}`,
                    borderRadius: 12,
                    boxShadow: isDark ? '0 2px 8px rgba(0,0,0,0.3)' : '0 1px 3px rgba(0,0,0,0.03)',
                }}
                bodyStyle={{ padding: '12px 18px 20px 18px' }}
            >
                <Tabs
                    activeKey={activeTab}
                    onChange={handleTabChange}
                    items={items}
                />
            </Card>
        </div>
    );
}
